import * as React from 'react'

/**
 * Client preference for desktop (browser) notifications when new ontology
 * notifications arrive. Independent of the Notification API permission —
 * both must be true for alerts to fire.
 *
 * Default is enabled so granting the browser permission is enough to start
 * receiving alerts; users can turn the preference off in Settings.
 */
const STORAGE_KEY = 'ontology-browser-notifications'
const CHANGE_EVENT = 'ontology-browser-notifications-change'

function readPreference(): boolean {
  if (typeof window === 'undefined' || !('localStorage' in window)) return true

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    // Explicit opt-out only. Absent / unknown → enabled (browser permission
    // still gates whether anything is actually shown).
    return stored !== 'false'
  } catch {
    return true
  }
}

function subscribePreference(listener: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined

  const handleStorage = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY) listener()
  }

  window.addEventListener(CHANGE_EVENT, listener)
  window.addEventListener('storage', handleStorage)

  return () => {
    window.removeEventListener(CHANGE_EVENT, listener)
    window.removeEventListener('storage', handleStorage)
  }
}

export function getBrowserNotificationsPreference(): boolean {
  return readPreference()
}

export function setBrowserNotificationsPreference(enabled: boolean): void {
  if (typeof window === 'undefined' || !('localStorage' in window)) return

  try {
    window.localStorage.setItem(STORAGE_KEY, enabled ? 'true' : 'false')
    window.dispatchEvent(new Event(CHANGE_EVENT))
  } catch {
    // Ignore storage failures; the setting is a client-side convenience.
  }
}

export function useBrowserNotificationsPreference(): boolean {
  return React.useSyncExternalStore(subscribePreference, readPreference, () => true)
}
